import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Grid from '@mui/material/Grid'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { MeasureVariant } from './functions/functions'
import { getPropertyRows } from './PropertyRows'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Status } from './hooks/useGetPropertyDetails'
import { useData } from './hooks/useData'
import { useHandlers } from './hooks/useHandlers'
import { DetailsDialog } from './measureDetails/DetailsDialog'
import { LocationSelector } from './measureDetails/LocationSelector'
import { NoDetails } from './NoDetails'

type Props = {
  locations: LocationDTO[]
  variant: MeasureVariant
  status: Status
  address?: string
  iconColor?: string
  entityId?: number
  entityType?: ENTITY_TYPES
}

const PropertyTypography = (props) => (
  <Typography
    variant="body2"
    component="span"
    sx={{ marginLeft: '16px', fontSize: '14px' }}
    {...props}
  />
)

export const PropertyDetailsDisplay: React.FC<Props> = ({
  locations,
  variant,
  status,
  address,
  entityId,
  entityType,
  iconColor,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<LocationDTO | null>(
    null,
  )
  const isMobile = useMediaQuery('(max-width:900px)')
  const data = useData()

  const { onClose, onOpen } = useHandlers(data)

  const structure = useMemo(() => {
    return selectedLocation?.propertyDetails?.buildings?.[0] ?? null
  }, [selectedLocation])

  useEffect(() => {
    if (locations.length > 0) {
      setSelectedLocation(
        locations.find((l) => l.primaryServiceLocation) ?? locations?.[0],
      )
    }
  }, [locations])

  const propertyRows = useMemo(
    () => getPropertyRows(structure, variant, 'medium', iconColor),
    [structure, variant, iconColor],
  )

  const isPending = useMemo(() => status === 'pending', [status])

  const isRowView = useMemo(
    () => isMobile || variant === 'card',
    [isMobile, variant],
  )

  const setLocation = useCallback(
    (loc: LocationDTO | null) => {
      if (loc) {
        setSelectedLocation(loc)
      }
    },
    [setSelectedLocation],
  )

  return (
    <>
      <Grid
        container
        direction="row"
        paddingLeft={1.6}
        paddingRight={1.6}
        sx={{ backgroundColor: '#fff' }}
        paddingTop={0}
        paddingBottom={1.4}
        rowGap={2}
      >
        {locations.length > 1 && (
          <Grid item xs={12} pt="2px" pr={1.6}>
            <LocationSelector
              locations={locations}
              locationId={selectedLocation?.id}
              onChange={setLocation}
            />
          </Grid>
        )}

        {status === 'fail' ||
        (selectedLocation?.propertyDetails === null && status === 'success') ? (
          <NoDetails />
        ) : (
          <>
            {propertyRows.map((row, index) => {
              return (
                <Grid
                  item
                  key={index}
                  {...(!isPending ? row.gridSize : { xs: isRowView ? 6 : 12 })}
                  display="flex"
                  alignItems="center"
                >
                  {isPending ? (
                    <Skeleton
                      variant="rectangular"
                      width={isRowView ? '98%' : '100%'}
                      sx={{ height: '24px', borderRadius: '4px' }}
                    />
                  ) : (
                    status === 'success' && (
                      <>
                        {row.icon}
                        <PropertyTypography>{row.label}</PropertyTypography>
                      </>
                    )
                  )}
                </Grid>
              )
            })}

            {status === 'success' && selectedLocation?.propertyDetails && (
              <Typography
                display="inline"
                onClick={onOpen}
                sx={{
                  color: '#005AA3',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
                data-test-id={`entity-primary-contact-drawer-toggle`}
              >
                View full property details
              </Typography>
            )}
          </>
        )}
      </Grid>

      <DetailsDialog
        open={data.open}
        onClose={onClose}
        locations={locations}
        locationId={selectedLocation?.id ?? 0}
        address={address}
        entityId={entityId}
        entityType={entityType}
      />
    </>
  )
}
